import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Box, Typography, Avatar, Button } from "@mui/material";
// import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import customer_avatar from "../../assets/images/customer_avatar.png";
import ImagePreviewModal from "./ImagePreviewModal";
import DocsSearch from "./DocsSearch";
import DocsTable from "./DocsTable";
import DocsPagination from "./DocsPagination";
import "./DocsNew.css";
import config from "../../config";
import { FaCalendarAlt } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import Header from "../Header/Header";
import Balls from "./Loading";
import { getApi } from "../AppApis";
import moment from "moment";
import DocumentTable from "./DocumentTable";
import { DatePicker, Space } from "antd";

const PAGE_SIZE = 10;

const UserDocuments = () => {
    const navigate = useNavigate();
    const [token, setToken] = useState(localStorage.getItem("authUser"));
    const [isLoading, setIsLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [availableStates, setAvailableStates] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const modalRef = useRef(null);

    const closeModal = () => {
        setModalIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        if (modalIsOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [modalIsOpen]);

    const fetchContractTemplates = async () => {
        setIsLoading(true);
        try {
            let url = `/documents/user-documents?page=${currentPage}`;
            const params = [];
            if (startDate) {
                params.push(`start_date=${encodeURIComponent(startDate)}`);
            }
            if (endDate) {
                params.push(`end_date=${encodeURIComponent(endDate)}`);
            }
            if (params.length > 0) {
                url += `&${params.join("&")}`;
            }

            const response = await getApi(url);
            if (response?.error === 401) {
                navigate("/login");
            } else if (response.response) {
                const data = response.response.data.data;
                setTemplates(data);
                setFilteredTemplates(data.filter((item) => item.doc_id));
                const uniqueAddresses = Array.from(new Set(data.map((doc) => doc.property_address).filter((address) => address)));
                const addressOptions = uniqueAddresses.map((address) => ({
                    label: address,
                    value: address,
                }));
                setCurrentPage(response.response.data.pagination.page);
                setTotalPages(response.response.data.pagination.total_pages);
                setAvailableStates(addressOptions);
            }

            if (response.error) {
                console.error("Error fetching templates:", response.error);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContractTemplates();
    }, [startDate, endDate, currentPage]);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (address) => {
        if (address === null || (selectedAddress && selectedAddress.value === address.value)) {
            setSelectedAddress(null);
            setFilteredTemplates(templates);
        } else {
            setSelectedAddress(address);
            handleFilterTemplates(searchText, address);
        }
        setCurrentPage(1); // Reset to first page when filter changes
        setAnchorEl(null);
    };

    const handleSearch = (text) => {
        setSearchText(text);
        handleFilterTemplates(text, selectedAddress);
    };

    const handleAddressClear = () => {
        setSelectedAddress(null);
        setFilteredTemplates(templates);
        setCurrentPage(1); // Reset to first page when filter is cleared
    };

    const handleFilterTemplates = (text, address) => {
        const normalizedSearchText = text.trim().toLowerCase();

        const filteredResults = templates.filter((template) => {
            const fileName = template.name.toLowerCase();
            const propertyAddress = template.property_address ? template.property_address.toLowerCase() : "";

            const matchesSearch = fileName.includes(normalizedSearchText) || propertyAddress.includes(normalizedSearchText);
            const matchesAddress = address ? template.property_address === address.label : true;
            const templateDate = new Date(template.uploaded_at);
            const templateDateOnly = new Date(templateDate.getFullYear(), templateDate.getMonth(), templateDate.getDate());

            const userStartDate = startDate ? new Date(startDate?.getFullYear(), startDate?.getMonth(), startDate?.getDate()) : null;
            const userEndDate = endDate ? new Date(endDate?.getFullYear(), endDate?.getMonth(), endDate?.getDate()) : null;

            if (userStartDate && !userEndDate) {
                return matchesSearch && matchesAddress && templateDateOnly >= userStartDate;
            } else if (!userStartDate && userEndDate) {
                return matchesSearch && matchesAddress && templateDateOnly <= userEndDate;
            } else if (userStartDate && userEndDate) {
                return matchesSearch && matchesAddress && templateDateOnly >= userStartDate && templateDateOnly <= userEndDate;
            } else {
                return matchesSearch && matchesAddress;
            }
        });

        setFilteredTemplates(filteredResults);
    };

    const handleStartDateChange = (dateString) => {
        setStartDate(dateString);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleEndDateChange = (dateString) => {
        setEndDate(dateString);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    useEffect(() => {
        document.title = `User Docs | ${config.get("COMPANY_NAME")}`;
    }, []);

    const handleDownload = useCallback(async (item) => {
        const path = `https://admin.onmls.com/api${item.url}`;

        try {
            // Get the token from localStorage (adjust the key if needed)
            const authUser = localStorage.getItem("authUser");
            const token = authUser?.replace(/^"|"$/g, "");

            const response = await fetch(path, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch file");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = item.name;
            document.body.appendChild(a);
            a.click();

            // Clean up
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading file:", error);
        }
    }, []);

    const handleQuickView = (item) => {
        const imageUrl = `https://admin.onmls.com/api${item.url}`;
        setPreviewImageUrl(imageUrl); // Set the URL of the file to be previewed
        setModalIsOpen(true); // Open the modal
    };

    const handleRedirectToContract = (id) => {
        navigate("/contract-generation/welcome");
    };

    const renderHighlightedText = (text, highlight) => {
        if (!highlight) return text;

        const parts = text.split(new RegExp(`(${highlight})`, "gi"));
        return parts.map((part, index) =>
            part.toLowerCase() === highlight.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: "yellow" }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const dataToShow = filteredTemplates;

    return (
        <>
            {" "}
            <div ref={modalRef}>
                <ImagePreviewModal
                    isOpen={modalIsOpen} // Modal open state
                    isPdf={true}
                    onClose={closeModal} // Function to close the modal
                    imageUrl={previewImageUrl} // URL of the image or PDF to preview
                />
            </div>
            <Header title="User Documents" />
            <Container className="tw-bg-background_color tw-p-0 tw-mt-5" maxWidth="">
                <Box className="tw-p-5  tw-overflow-hidden" sx={{ display: "flex", flexDirection: "column", minHeight: "80vh" }}>
                    <Box className=" tw-w-full tw-flex tw-flex-col">
                        <Box
                            className=" max-sm:tw-flex-wrap max-md:tw-flex-wrap max-md:tw-gap-2"
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 3,
                                mt: { xs: 0, sm: 0, md: 7 }, // 👈 Only add marginTop on desktop
                            }}
                        >
                            <Box
                                className={"max-md:tw-w-full max-sm:tw-w-full tw-py-[5px]"}
                                sx={{ position: "relative", display: "flex", gap: 2, borderRadius: "8px", height: "45px" }}
                            >
                                <DatePicker
                                    placeholder="Start Date"
                                    className="tw-w-full lg:tw-min-w-[100px]"
                                    onChange={(date, dateString) => handleStartDateChange(dateString)}
                                    format="YYYY-MM-DD"
                                />
                                <DatePicker
                                    placeholder="End Date"
                                    className="tw-w-full lg:tw-min-w-[100px]"
                                    onChange={(date, dateString) => handleEndDateChange(dateString)}
                                    format="YYYY-MM-DD"
                                />{" "}
                            </Box>
                        </Box>
                        <div className="tw-flex tw-flex-col tw-rounded-lg tw-gap-4 tw-w-full tw-bg-white">
                            <div className="tw-p-2">
                                <DocsSearch
                                    searchText={searchText}
                                    setSearchText={setSearchText}
                                    handleSearch={handleSearch}
                                    handleMenuClick={handleMenuClick}
                                    handleMenuClose={handleMenuClose}
                                    anchorEl={anchorEl}
                                    availableStates={availableStates}
                                    selectedAddress={selectedAddress}
                                    handleAddressClear={handleAddressClear}
                                    setSelectedAddress={setSelectedAddress}
                                />
                            </div>
                            {isLoading ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "90vh",
                                    }}
                                >
                                    <Balls />
                                </Box>
                            ) : dataToShow.length > 0 ? (
                                <div>
                                    <DocumentTable
                                        isLoading={isLoading}
                                        dataToShow={dataToShow}
                                        setDataToShow={setFilteredTemplates}
                                        renderHighlightedText={renderHighlightedText}
                                        handleDownload={handleDownload}
                                        handleQuickView={handleQuickView}
                                        handleRedirectToContract={handleRedirectToContract}
                                        searchText={searchText}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        totalPages={totalPages}
                                        data={filteredTemplates}
                                    />
                                </div>
                            ) : (
                                <Box className="BoxOops" sx={{ flexGrow: 1 }}>
                                    {!isLoading && dataToShow.length === 0 && (
                                        <Typography
                                            className="Oops"
                                            sx={{
                                                fontSize: "16px",
                                                fontWeight: "400",
                                                fontFamily: "Roboto",
                                                color: "#6B7280",
                                                textAlign: "center",
                                                mt: 4,
                                            }}
                                        >
                                            “Oops! No documents match your current filters or search criteria. Please try adjusting your filters or
                                            search terms to find relevant results.”
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </div>
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default UserDocuments;
